// Quote Builder — SJB cabinet picker.
//
// Replaces the direct-add that clicking an SJB search hit used to do. Nothing
// reaches the quote until Confirm: the retailer sets (or accepts) the board
// colour, hinge finish and drawer system for THIS cabinet, picks a hinge side
// where the unit has one, and chooses the frontal.
//
// ── What Confirm commits ──────────────────────────────────────────────────
// Up to three lines, sharing an `sjbGroupId` so the quote can render them
// together:
//
//   1. the cabinet   — carcass (discounted) + hardware (at list), as before
//   2. the frontal   — an ordinary `doors` line
//   3. drilling      — only when hinge drilling is on and the supplier
//                      charges for it
//
// They are separate lines on purpose. Folding the frontal into the cabinet
// line would take it off the door pricing path — its own supplier discount via
// resolveDiscPct, and the per-hole drilling charge — and that is a pricing
// behaviour change. This module therefore builds the frontal line the same way
// door-picker.ts does and changes no pricing logic at all.
//
// ── Frontal sources ───────────────────────────────────────────────────────
// Driven by GET /sjb-catalogue/frontal-suppliers, which is server config, not
// code. Only catalogues SJB have verified appear. Ranges, colours and sizes
// then come from the existing supplier-agnostic
// /catalogue/door-ranges + /catalogue/doors/sizes, so a newly-listed
// catalogue needs no change here.

import { api } from "@tkos/api-client";
import { repaintPreservingScroll } from "@tkos/shared-ui";
import { setConfiguratorInProgress } from "./picker-selection";
import { alertDialog } from "@tkos/shared-ui";
import type { QuoteItem, TkcRange } from "./types";
import type { QuoteStore } from "./state";
import { escapeHtml, fmtGbp, newId } from "./formatters";
import { recalcItem } from "./pricing";
import { loadDiscountsFor, resolveDiscPct } from "./discounts";
import { loadDrillingChargeFor, resolveDrillingCharge } from "./drilling-charge";
import { aggregateDrilling } from "./drilling-aggregate";
import { mountColourCombobox, type ColourComboHandle } from "./sjb-colour-combobox";
import {
  renderExtrasSection,
  wireExtrasSection,
  fetchExtras,
  inferExtraCategory,
  type ExtraItem,
} from "./door-extras-section";
import {
  renderColourSelect,
  renderTierBadge,
  type DoorType,
} from "./door-colour-select";
import {
  renderDrillingBlock,
  wireDrillingBlock,
  holePositionText,
  blankDrillingSpec,
  type DrillingSpec,
} from "./door-drilling-block";
import {
  getSjbDefaults,
  setSjbDefaults,
  edgingKeyForLabel,
  SJB_EDGING_MATCHING,
  loadSjbColours,
  loadSjbFrontalSuppliers,
  priceSjbLine,
  sjbLineToItem,
  type SjbColour,
  type SjbFrontalSupplier,
  type SjbSideChoice,
  type SjbLeaf,
  type SjbPricedLine,
} from "./sjb-catalogue";

export interface SjbPickerHandle { unmount(): void; }

/** Hinge-side options, matching DTN's row exactly (cabinet-picker.ts:546-549).
 *  Shown in full even where the recipe lists only Left|Right: "Both" and
 *  "None" are legitimate answers for a door the retailer wants unhanded or
 *  undrilled, and a row that changes shape between suppliers is the
 *  inconsistency this restyle exists to remove. Which units get the row at
 *  all still comes from the server's handingChoice. */
const HANDING_OPTIONS: readonly string[] = ["Left", "Right", "Both", "None"];

/** How far a catalogue door may sit from the recipe's leaf and still count as
 *  THE size rather than a near-miss, per dimension. 3mm covers the 295/297
 *  spread seen around a nominal 296 without reaching a genuinely different
 *  door — the next stocked width is 50mm away. A starting figure, not a
 *  measured one; change it here. */
const SIZE_TOLERANCE_MM = 3;

interface DoorSize {
  sku: string; name?: string; h: number; w: number; price: number;
  /** "standard" | "curved" | "glazed" | "frame", from catalogue.js. Curved
   *  rows carry w = 0 by design — a curved door has no single flat width —
   *  so `type` is the only reliable way to tell one apart. */
  type: string; category?: string; label?: string;
  /** The supplier's discount code (PWS "BMV"), served since 2026-10-01. */
  discountCode?: string;
}

/**
 * One frontal on this cabinet: a door or a drawer front, with the size the
 * retailer has chosen for it.
 *
 * The cabinet is a LIST of leaves, and modelling it as a single "size"
 * applied to every door was the shared root cause of three separate reported
 * faults — even doors on asymmetric units, "715 x 0" on curved units, and a
 * drawerline auto-selecting two identical doors instead of a drawer front
 * above a door. Each leaf now carries its own selection, its own suggestion
 * and its own price.
 */
interface FrontalLeaf {
  /** Position in the recipe's leaf list — the order they sit on the unit,
   *  drawer front above door. Also the key into st.leafSkus. */
  index: number;
  kind: "door" | "front";
  /** What the leaf physically is, for wording only. Falls back to `kind` on
   *  an API that does not send it. */
  part: "door" | "drawer-front" | "filler" | "lift-up" | "top-hung";
  /** Holes in the TOP edge, on a top-hung flap only; 0 on everything else.
   *  Read from the recipe via the API rather than derived from the leaf's
   *  height — the side-hung height tiers do not apply to a flap that pivots
   *  on its top edge. See SjbLeaf.drilling. */
  topHungHoles: number;
  /** Index into drillSpecs; -1 for a drawer front, which is screwed to the
   *  drawer box and has nothing to bore. */
  doorIndex: number;
  /** What the recipe says this leaf is, before any override. */
  recipeH: number;
  recipeW: number;
  curved: boolean;
  /** The catalogue size chosen for it, if any. */
  size: DoorSize | null;
  /** Effective dimensions — the chosen size where there is one, else the
   *  recipe's. Width stays with the recipe for a curved leaf: the catalogue
   *  row carries w = 0, and 0 is not a width a fabricator can cut to. */
  h: number;
  w: number;
  fromSize: boolean;
}

interface PickerState {
  boardKey: string;
  boardLabel: string;
  hingeFinish: "standard" | "onyx";
  drawerSystem: "antaro" | "legrabox";
  handing: string;
  /** Column 7. "" = None, which is a real answer: most units that CAN take a
   *  reduced gable will not have one, so nothing is pre-selected. */
  reducedGable: string;
  /** Column 9. "" = not specified yet. Deliberately NOT defaulted to Left:
   *  every one of these 104 units HAS a blanked return, so the side is a real
   *  manufacturing decision and guessing it ships half of them mirrored. */
  blankingPanel: string;
  edgingColour: string;
  /** Editable manufacturing dimensions. Width is capped at the SJB Code's
   *  standard — you can scribe a unit down, you cannot make it wider without
   *  it being a different unit. */
  width: string;
  height: string;
  depth: string;
  qty: number;
  zoneId: string;
  frontalSlug: string;
  frontalLabel: string;
  frontalLoadFailed: boolean;
  frontalRange: string;
  frontalColour: string;
  frontalTier: DoorType | "";
  /** Chosen catalogue SKU per LEAF, keyed by the leaf's index in the recipe.
   *
   *  One dropdown per leaf replaced one dropdown per cabinet. A 1000mm unit
   *  can need a 396 beside a 596; a drawerline needs a 140mm drawer front
   *  above a 570mm door; a curved base needs a curved leaf beside a flat
   *  one. None of those is expressible as a single size applied to every
   *  leaf, and the old model quietly produced identical doors instead. */
  leafSkus: Record<number, string>;
  /** Whether this cabinet takes frontals at all. Seeded from the quote
   *  default and written back to it — cabinets-only is a property of the
   *  job, not of each cabinet. */
  frontalOn: boolean;
  /** Whether picking a Range/Colour writes it back as the quote's default.
   *  Mirrors the TKC Door Configurator's "Remember". */
  rememberFrontal: boolean;
  /** Manufacturing / site note for THIS cabinet.
   *
   *  Pre-filled from the recipe's own `notes` column and editable from there,
   *  the same control the DTN picker has had (cabinet-picker.ts:598) rather
   *  than a second one that drifts. `notesSeeded` records which SJB Code the
   *  pre-fill came from, so re-pricing (a colour change) does not overwrite
   *  something the retailer has typed, while switching cabinet does re-seed. */
  notes: string;
  notesSeeded: string;
  drilling: boolean;
  busy: boolean;
  error: string;
  widthClamped: boolean;
}

export function mountSjbCabinetPicker(
  host: HTMLElement,
  store: QuoteStore,
  sjbCode: string,
  onDone: () => void,
): SjbPickerHandle {
  let mounted = true;
  let colours: SjbColour[] = [];
  let frontalSuppliers: SjbFrontalSupplier[] = [];
  let ranges: TkcRange[] = [];
  /** In flight for the chosen catalogue. An empty `ranges` means three
   *  different things — no catalogue chosen yet, still fetching, or a
   *  catalogue that genuinely carries none — and the disabled Range select
   *  has to say which. Without this the select would read "No ranges in this
   *  catalogue" for the duration of the fetch, i.e. state something about the
   *  catalogue that nobody had checked yet. */
  let rangesLoading = false;
  let sizes: DoorSize[] = [];
  let priced: SjbPricedLine | null = null;
  let combo: ColourComboHandle | null = null;
  let edgingCombo: ColourComboHandle | null = null;
  /** One drilling spec per door leaf, index-aligned with
   *  priced.drilling.doors. A 1000HL has two — left and right can be mirrored
   *  rather than assumed symmetric, which is the whole point of doing this
   *  per door instead of once per cabinet. */
  let drillSpecs: DrillingSpec[] = [];
  /** "<count>@<height>" the specs were last seeded from. */
  let drillSeededFor = "";
  /** Panels & Accessories for the bound range — collapsed by default. */
  let extras: ExtraItem[] = [];
  let extrasOpen = false;
  let extrasLoading = false;
  const extrasQty: Record<string, number> = {};
  /** Extras the retailer has picked. Committed with Confirm alongside the
   *  cabinet rather than dropped straight onto the quote — nothing in this
   *  picker reaches the quote until Confirm, and an extras row should not be
   *  the one exception. */
  const pendingExtras: ExtraItem[] = [];

  const d = getSjbDefaults(store);
  const st: PickerState = {
    boardKey: d.boardKey,
    boardLabel: d.boardLabel,
    hingeFinish: d.hingeFinish,
    drawerSystem: d.drawerSystem,
    handing: "",
    reducedGable: "",
    blankingPanel: "",
    edgingColour: d.edgingColour,
    width: "",
    height: "",
    depth: "",
    qty: 1,
    zoneId: store.get().zones[0]?.id ?? "",
    // Seeded from the quote default, so the second cabinet opens on the
    // catalogue the first one used. NOT gated by rememberFrontal — see
    // SjbDefaults.frontalSlug for why the catalogue is remembered
    // unconditionally while range and colour are opt-out.
    //
    // The label is deliberately left blank here and resolved once the live
    // supplier list arrives: it is server config, and a cached label can go
    // stale against a quote that outlives it.
    frontalSlug: d.frontalSlug || "",
    frontalLabel: "",
    frontalLoadFailed: false,
    // Seeded from the quote's remembered frontal, so the second cabinet on a
    // kitchen opens on the range and colour the first one used.
    frontalRange: d.rememberFrontal ? (d.frontalRange || "") : "",
    frontalColour: d.rememberFrontal ? (d.frontalColour || "") : "",
    frontalTier: (d.rememberFrontal ? (d.frontalTier || "") : "") as DoorType | "",
    leafSkus: {},
    frontalOn: d.frontalOn !== false,
    rememberFrontal: d.rememberFrontal !== false,
    notes: "",
    notesSeeded: "",
    drilling: true,
    busy: false,
    error: "",
    widthClamped: false,
  };

  // ── data ────────────────────────────────────────────────────────────────

  async function reprice(): Promise<void> {
    if (!st.boardKey) { priced = null; paint(); return; }
    st.busy = true; paint();
    try {
      priced = await priceSjbLine(sjbCode, {
        boardKey: st.boardKey, hingeFinish: st.hingeFinish, drawerSystem: st.drawerSystem,
      });
      // Seed the hinge side once, from whatever the catalogue offers.
      if (!st.handing && priced.handingChoice?.choice) {
        st.handing = priced.handingChoice.options[0] ?? "";
      }
      // Seed the note from the recipe, once per SJB Code. Guarded so a
      // reprice (which fires on every colour change) cannot wipe a note the
      // retailer has edited.
      if (st.notesSeeded !== sjbCode) {
        st.notesSeeded = sjbCode;
        st.notes = priced.notes ?? "";
      }
      // Seed the editable dimensions from the SJB Code's own, so the fields
      // start at standard and any deviation is deliberate.
      seedDrillSpecs();
      const std = standardDims();
      if (!st.width && std.w) st.width = String(std.w);
      if (!st.height && std.h) st.height = String(std.h);
      if (!st.depth && std.d) st.depth = String(std.d);
      st.error = "";
    } catch {
      priced = null;
      st.error = "Could not reach the SJB catalogue.";
    } finally {
      st.busy = false; paint();
    }
  }

  async function loadRanges(slug: string): Promise<void> {
    ranges = [];
    rangesLoading = true;
    paint();
    try {
      const data = await api<{ ranges?: TkcRange[] }>(
        `/catalogue/door-ranges?supplier=${encodeURIComponent(slug)}`);
      ranges = data.ranges ?? [];
    } catch { ranges = []; } finally { rangesLoading = false; }
    void loadDiscountsFor(st.frontalLabel || slug.toUpperCase());
    void loadDrillingChargeFor(slug);
    // A remembered range is only half the state — its colours live on the
    // range row and its sizes need fetching. Without this the picker opens
    // showing "Cartmel · White" with an empty Size list, which looks broken
    // and is worse than not remembering at all. Dropped if the remembered
    // range no longer exists in the catalogue.
    if (st.frontalRange) {
      if (!ranges.some((r) => r.name === st.frontalRange)) {
        st.frontalRange = ""; st.frontalColour = ""; st.frontalTier = "";
      } else if (st.frontalColour && !sizes.length) {
        void loadSizes();
        return;
      }
    }
    paint();
  }

  /**
   * The catalogue rows a given leaf may legitimately be made from.
   *
   * Shape first, size second. TKC list curved doors with w = 0
   * (catalogue.js:1282), so scoring them on width would rank every curved
   * door as a perfect match for a curved leaf AND a hopeless one for a flat
   * leaf of the same height — and, worse, would offer a flat 256mm door as
   * the answer to a curved leaf. A leaf's shape is known from the recipe, so
   * use it.
   *
   * An empty result for a curved leaf is a real answer — "this range does not
   * stock a curved door" — and is said out loud rather than backfilled with
   * flat doors.
   */
  function sizesFor(leaf: { curved: boolean }): DoorSize[] {
    const pool = sizes.filter((z) => (z.type === "curved") === leaf.curved);
    if (pool.length) return pool;
    // Flat leaf, and every row in the range is curved: fall back rather than
    // strand the retailer with an empty list. A curved leaf gets no such
    // fallback — a flat door is not a curved door.
    return leaf.curved ? [] : sizes;
  }

  /**
   * The frontal a given leaf actually wants.
   *
   * Taken from the recipe's OWN leaf — "715 x 596" on a 600HL — not guessed
   * from the carcass width, which is 600 and would never match a 596 door.
   * Exact height+width wins; otherwise the nearest by combined difference, so
   * a range that stocks 715x597 still lands on the right row.
   *
   * This is a suggestion, never a lock: it pre-selects, labels itself, and
   * leaves the whole list browsable.
   */
  function suggestedSizeFor(
    leaf: { recipeH: number; recipeW: number; curved: boolean },
  ): { size: DoorSize; exact: boolean; delta: number } | null {
    const pool = sizesFor(leaf);
    if (!pool.length) return null;

    const scored = pool
      .map((z) => ({
        z,
        dh: Math.abs(z.h - leaf.recipeH),
        // A curved row has no width to compare. Score it on height alone
        // rather than against a 0 that would swamp the height term.
        dw: leaf.curved ? 0 : Math.abs(z.w - leaf.recipeW),
      }))
      .sort((a, b) => (a.dh + a.dw) - (b.dh + b.dw));
    const best = scored[0];
    if (!best) return null;

    // Treated as THE size when both dimensions land within tolerance, not only
    // on a literal equality: suppliers list nominally-identical doors a
    // millimetre or two apart (295 or 297 against a 296 leaf), and a strict
    // match sends the retailer hunting through the full list for a door that
    // is, in every practical sense, the right one.
    const within = best.dh <= SIZE_TOLERANCE_MM && best.dw <= SIZE_TOLERANCE_MM;
    return { size: best.z, exact: within, delta: best.dh + best.dw };
  }

  async function loadSizes(): Promise<void> {
    sizes = [];
    if (!st.frontalSlug || !st.frontalRange) { paint(); return; }
    // Fetch the tier the retailer actually chose. This used to be hardcoded to
    // "stock" while still passing the colour, and /doors/sizes filters strictly
    // on both — so picking any non-stock colour returned ZERO sizes and the
    // frontal simply could not be added. It stayed hidden because TKC's PTO
    // colours come from the supplier-level palette, which nobody could reach a
    // size for either way; PWS, whose PTO and Paint Plus colours are real
    // product rows, makes it reachable and obvious.
    //
    // `signature` has no size rows of its own — it is a stock-priced palette —
    // so it reads from stock, matching door-picker.ts:615.
    const fetchTier: DoorType = (st.frontalTier || "stock") as DoorType;
    const fetchType = fetchTier === "signature" ? "stock" : fetchTier;
    const fetchSizesFor = async (colour: string): Promise<DoorSize[]> => {
      const qs = new URLSearchParams({
        supplier: st.frontalSlug, range: st.frontalRange, type: fetchType,
      });
      if (colour) qs.set("colour", colour);
      try {
        const data = await api<{ sizes?: DoorSize[] }>(`/catalogue/doors/sizes?${qs.toString()}`);
        return Array.isArray(data.sizes) ? data.sizes : [];
      } catch { return []; }
    };
    // Colour first, then the whole tier if that finds nothing — the same
    // two-step door-picker.ts:621-631 uses. A palette colour (TKC PTO) has no
    // product rows to match, so only the tier-wide fetch can answer it; a real
    // product colour (PWS "Painted") answers on the first call and stays
    // scoped to the colour the retailer picked.
    sizes = await fetchSizesFor(st.frontalColour);
    if (!sizes.length && st.frontalColour) sizes = await fetchSizesFor("");
    // Pre-select each leaf's own size so the common case is one fewer
    // decision per leaf. Only where the retailer has not already chosen, and
    // only on an EXACT match (within SIZE_TOLERANCE_MM).
    //
    // A merely-nearest row is offered and starred but never auto-committed:
    // once each leaf picks independently, "nearest" can be absurd. A 1425mm
    // larder door in a range that stocks nothing above 715 would silently
    // select a 715 — and, because the drilling follows the selected size,
    // silently drop that door from three hinge holes to two.
    for (const leaf of frontalLeaves()) {
      if (st.leafSkus[leaf.index]) continue;
      const s2 = suggestedSizeFor(leaf);
      if (s2?.exact) st.leafSkus[leaf.index] = s2.size.sku;
    }
    paint();
    void loadExtras();
  }

  async function loadExtras(): Promise<void> {
    if (!st.frontalSlug || !st.frontalRange) { extras = []; paint(); return; }
    extrasLoading = true; paint();
    // Non-stock panels are tier-scoped, not colour-scoped — the same rule the
    // door picker applies, so pass colour only for stock.
    const tier = (st.frontalTier || "stock") as DoorType;
    extras = await fetchExtras(st.frontalSlug, st.frontalRange, tier,
                               tier === "stock" ? st.frontalColour : "");
    extrasLoading = false; paint();
  }

  /** Persist the chosen Range/Colour as the quote's default, when the
   *  retailer has asked us to. Range and Colour only — a size belongs to the
   *  cabinet it was measured for. Same shape as door-picker.ts:568. */
  function rememberFrontal(): void {
    if (!st.rememberFrontal) return;
    setSjbDefaults(store, {
      frontalRange: st.frontalRange,
      frontalColour: st.frontalColour,
      frontalTier: st.frontalTier,
    });
  }

  /** An extra is its own quote line, tagged into the cabinet's group so it
   *  travels with it. It keeps the ordinary door-supplier pricing path — the
   *  same reason the frontal is a separate line. */
  function addExtra(x: ExtraItem, groupId: string): QuoteItem {
    const discPct = resolveDiscPct(st.frontalLabel, x.category || null, st.frontalRange, {
      discountCode: x.discountCode ?? null, doorColour: st.frontalColour || null, code: x.sku,
    }) ?? 0;
    const item: QuoteItem = {
      id: newId("i"), category: inferExtraCategory(x.name), supplier: st.frontalLabel,
      code: x.sku, desc: x.name, qty: extrasQty[x.id] ?? 1,
      width: x.w || 0, height: x.h || 0, depth: x.d || 0,
      notes: `For ${sjbCode}${x.category ? ` · ${x.category}` : ""}`,
      listPrice: Number(x.price) || 0, discPct,
      tradePrice: 0, markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0, markupGbp: 0, retailPrice: 0,
      doorRange: st.frontalRange, doorColour: st.frontalColour,
      sjbGroupId: groupId,
    };
    return recalcItem(item, null);
  }

  // ── derived ─────────────────────────────────────────────────────────────

  const currentRange = (): TkcRange | undefined =>
    ranges.find((r) => r.name === st.frontalRange);

  /** Every non-empty tier, in the order a retailer thinks about cost.
   *  Ranges from before 2026-05-26 carry no coloursByType, in which case the
   *  flat list is treated as stock — the same fallback the door picker makes. */
  function colourGroups(): Array<{ tier: DoorType; colours: readonly string[] }> {
    const r = currentRange();
    if (!r) return [];
    const byType = r.coloursByType;
    if (!byType) return [{ tier: "stock", colours: r.colours ?? [] }];
    // Order is the order the retailer reads them in: stocked first, then
    // Paint Plus (stocked PRICE, longer lead) as the nearest step up, then the
    // tiers that also move the price. Tiers are listed explicitly rather than
    // iterated so this order is deliberate — but every DoorType must appear,
    // or its colours are silently unreachable, which is exactly what happened
    // to `paintplus` here.
    return ([
      ["stock", byType.stock], ["paintplus", byType.paintplus],
      ["signature", byType.signature],
      ["pto", byType.pto], ["bespoke", byType.bespoke], ["primed", byType.primed],
    ] as Array<[DoorType, string[]]>)
      .filter(([, c]) => (c ?? []).length > 0)
      .map(([tier, colours]) => ({ tier, colours }));
  }
  /**
   * Every leaf on this cabinet, in the order the recipe lists them — which is
   * the order they sit on the unit, drawer front above door.
   *
   * `leaves` arrives from the API (modules/sjb_catalogue.js drillingForRecipe).
   * An API older than 2026-09-05 does not send it, and in that case we fall
   * back to `doors` alone rather than inventing drawer fronts we have no
   * dimensions for — absent data must not render as a positive claim, the
   * same rule the handing and frontal-supplier branches follow.
   */
  function recipeLeaves(): SjbLeaf[] {
    const dr = priced?.drilling;
    if (!dr) return [];
    if (dr.leaves?.length) return dr.leaves;
    return (dr.doors ?? []).map((leaf, i) => ({
      index: i, height: leaf.height, width: leaf.width,
      curved: !!leaf.curved, kind: "door" as const,
    }));
  }

  /**
   * The leaves this cabinet will actually be made and drilled for.
   *
   * The recipe's leaf is the DEFAULT, but the retailer can pick a different
   * catalogue size for ANY leaf independently — and it is the door being made
   * that gets drilled, not the one the recipe assumed.
   *
   * The old "apply the one chosen size to every door, unless the heights are
   * mixed, in which case apply it to the closest one" rule is gone with the
   * single dropdown that forced it. Each leaf owns its size, so there is
   * nothing left to disambiguate.
   */
  function frontalLeaves(): FrontalLeaf[] {
    let doorOrdinal = 0;
    return recipeLeaves().map((leaf) => {
      const doorIndex = leaf.kind === "door" ? doorOrdinal++ : -1;
      const sku = st.leafSkus[leaf.index] ?? "";
      const size = sizes.find((z) => z.sku === sku) ?? null;
      return {
        index: leaf.index,
        kind: leaf.kind,
        // An older API sends no `part`; "drawer front" was what it said then,
        // so that stays the fallback rather than a new guess.
        part: leaf.part ?? (leaf.kind === "door" ? "door" : "drawer-front"),
        // Only ever non-zero where the API said this leaf is top-hung AND gave
        // a count. An API older than 2026-09-10 sends neither, so it stays 0
        // and the flap reads exactly as it did before — no invented holes.
        topHungHoles: leaf.part === "top-hung" ? (leaf.drilling?.holes ?? 0) : 0,
        doorIndex,
        recipeH: leaf.height,
        recipeW: leaf.width,
        curved: !!leaf.curved,
        size,
        h: size ? size.h : leaf.height,
        // A curved catalogue row carries w = 0. Keep the recipe's width —
        // resolved server-side from the carcass — so the line, the cut sheet
        // and the drilling summary all carry a number a fabricator can use.
        w: size && !(size.type === "curved") ? size.w : leaf.width,
        fromSize: !!size,
      };
    });
  }

  const doorLeaves = (): FrontalLeaf[] => frontalLeaves().filter((l) => l.kind === "door");

  /** "715 × 496mm", or "715mm Curved" where there is no flat width to state.
   *  Same wording as the door picker (door-picker.ts:746) so the two
   *  catalogues describe a curved door identically. */
  function dimsLabel(h: number, w: number, curved: boolean): string {
    return curved ? `${h}mm Curved${w ? ` (${w}mm run)` : ""}` : `${h} × ${w}mm`;
  }

  /** "Door 2 of 2" / "Drawer front 1 of 2" / "Curved door". Numbered within
   *  its own kind: on a drawerline, "Door 1" is the door, not the second
   *  leaf on the cabinet. */
  function leafName(leaf: FrontalLeaf): string {
    // Numbered within its own PART, not within `kind`. On a Tall Double Oven
    // Housing + 2 Drawers the leaves are a door, a filler and two drawer
    // fronts — numbering by kind made that read "Drawer front 1/2/3 of 3",
    // which named the filler as a drawer and then miscounted the drawers.
    const sibs = frontalLeaves().filter((l) => l.part === leaf.part);
    const ord = sibs.findIndex((l) => l.index === leaf.index) + 1;
    const noun = leaf.part === "filler" ? "Oven filler"
      // "Top-hung" is the word the manufacturer needs: it says drill the TOP
      // of the unit, not the side. Steve, 2026-09-10. A bifold pair stays
      // "Lift-up flap" — it is hinged, but not top-hung to a spec anyone has
      // given us, and naming it top-hung would tell the factory otherwise.
      : leaf.part === "top-hung" ? "Top-hung flap"
      : leaf.part === "lift-up" ? "Lift-up flap"
      : leaf.part === "drawer-front" ? "Drawer front"
      : leaf.curved ? "Curved door" : "Door";
    return sibs.length > 1 ? `${noun} ${ord} of ${sibs.length}` : noun;
  }

  /** True when this door sits under a drawer front. Worth saying out loud: a
   *  570mm door on a 720mm carcass looks like an error next to the 715mm
   *  full-height doors everywhere else in the range, and it is not. */
  function isPartHeightDoor(leaf: FrontalLeaf): boolean {
    // A drawer front ABOVE this door is what makes it part-height, so the
    // test is positional: the recipe lists leaves top to bottom.
    //
    // Two wrong labels came out of ignoring that. "Any short leaf" made an
    // oven filler above a full-height 715mm door read as a drawer front on a
    // unit with no drawer. "Any drawer front anywhere" then made a Tall
    // Double Oven Housing's 645mm door part-height because a 283mm drawer
    // front sits BELOW it — and did the same to every dresser, whose 895mm
    // doors sit above their drawers, not beneath them.
    return leaf.kind === "door"
      && frontalLeaves().some((l) => l.part === "drawer-front" && l.index < leaf.index);
  }

  /** The lower-case noun for a leaf, for use inside a sentence. */
  function leafNoun(leaf: FrontalLeaf): string {
    return leaf.part === "filler" ? "oven filler"
      : leaf.part === "top-hung" ? "top-hung flap"
      : leaf.part === "lift-up" ? "lift-up flap"
      : leaf.part === "drawer-front" ? "drawer front" : "door";
  }

  /**
   * Pre-fill one drilling block per door from the height-derived spec.
   *
   * The auto-calculated count and positions become the block's starting
   * values rather than leaving it blank — the retailer adjusts from a correct
   * default instead of typing one. Rebuilt only when the door count changes,
   * so a repriced colour does not discard hand-entered positions.
   */
  function seedDrillSpecs(): void {
    // Doors only. A drawer front is screwed to the drawer box — there is
    // nothing to bore — so it gets no block and no holes.
    const doors = doorLeaves();
    // Reseed when the door COUNT or its HEIGHT changes. Keying on count alone
    // meant picking a different size left the blocks on the old door's
    // positions — the whole of bug B.
    const key = doors.map((d) => d.h).join("/");
    if (drillSeededFor === key) return;
    drillSeededFor = key;
    const wall = /wall|bridge|cornice/i.test(priced?.desc ?? "");
    drillSpecs = doors.map((d, i) => {
      const auto = drillingForHeight(d.h);
      const pos = auto.positions;
      return blankDrillingSpec({
        mode: "hinge",
        unitType: wall ? "wall" : "base",
        // Alternate sides on a multi-door unit: a pair of doors hangs
        // outward, so defaulting both to Left would be wrong on every double.
        side: doors.length > 1 && i % 2 === 1 ? "R" : "L",
        holeQty: auto.holes,
        topMm: pos[0] ?? 98,
        bottomMm: d.h - (pos[pos.length - 1] ?? d.h - 98),
        midMm: pos.slice(1, -1),
      });
    });
  }

  /** Hole count + positions for a door of this height. Mirrors the server's
   *  drillingForDoor (modules/sjb_catalogue.js) so a size the retailer picks
   *  gets the same treatment as the recipe's own leaf: standard 2 at 98/H-98,
   *  1425mm 3 with a 713 centre, 1900mm+ 4 at 98/748 mirrored. */
  function drillingForHeight(h: number): { holes: number; positions: number[] } {
    if (h >= 1900) return { holes: 4, positions: [98, 748, h - 748, h - 98] };
    if (h >= 1425) return { holes: 3, positions: [98, 713, h - 98] };
    return { holes: 2, positions: [98, h - 98] };
  }

  /** Total holes across every door on this cabinet.
   *
   *  Derived from each door's HEIGHT, not from the hinge quantity. An earlier
   *  cut divided the hinge count by the door count; that happens to agree at
   *  1965mm and 2145mm but is wrong at 1425mm, and it says nothing about where
   *  the holes go — which is what manufacture actually needs. */
  /** Holes bored into a TOP-HUNG flap's top edge. Separate from drillSpecs,
   *  which are indexed by DOOR and hold an editable side-hung spec; a
   *  top-hung flap is a `front`, has no drillSpec, and its count comes
   *  straight from the recipe. */
  function topHungHoles(): number {
    return frontalLeaves().reduce((n, l) => n + l.topHungHoles, 0);
  }

  function totalHoles(): number {
    // Both, because a unit can in principle carry doors AND a top-hung flap —
    // the microwave wall housing is a flap plus a filler today, but nothing in
    // the file forbids the combination, and a charge that silently dropped one
    // of them would under-bill by exactly the holes nobody counted.
    if (drillSpecs.length) {
      return drillSpecs.reduce((n, sp) => n + (sp.mode === "hinge" ? sp.holeQty : 0), 0)
             + topHungHoles();
    }
    return (priced?.drilling?.specHoles ?? 0) + topHungHoles();
  }

  /** Per-door "3 holes at 98, 713, 1327mm" strings for the spec summary.
   *  Drawer fronts are listed too, saying plainly that they take no holes —
   *  a leaf missing from this list reads as an oversight. */
  function drillingRows(): string[] {
    return frontalLeaves().map((leaf) => {
      const name = leafName(leaf);
      const dims = dimsLabel(leaf.h, leaf.w, leaf.curved);
      // A top-hung flap IS drilled, just not by the side-hung rule, and it is
      // certainly not "fixed to the drawer box" — there is no box. Its holes
      // come from the recipe and its positions are deliberately unstated.
      if (leaf.part === "top-hung" && leaf.topHungHoles > 0) {
        return `${name} (${dims}) — top-hung, ${leaf.topHungHoles} hole${
          leaf.topHungHoles === 1 ? "" : "s"} in the top of the unit`;
      }
      if (leaf.kind === "front") {
        return `${name} (${dims}) — ${leaf.part === "lift-up"
          ? "no drilling"
          : "no drilling, fixed to the drawer box"}`;
      }
      const sp = drillSpecs[leaf.doorIndex];
      if (!sp || sp.mode !== "hinge") return `${name} (${dims}) — no drilling`;
      const hand = sp.side === "L" ? "Left" : sp.side === "R" ? "Right" : "Top";
      return `${name} (${dims}, ${sp.unitType}, ${hand}) — ${holePositionText(sp)}`;
    });
  }

  /** The SJB Code's own dimensions: "720 x 600 x 570" is height x width x depth. */
  function standardDims(): { h: number; w: number; d: number } {
    const m = (priced?.dims ?? "").match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/i);
    return m
      ? { h: Number(m[1]), w: Number(m[2]), d: Number(m[3]) }
      : { h: 0, w: 0, d: 0 };
  }

  /**
   * Height/depth typed here are a MANUFACTURING note, nothing else.
   *
   * Width is absent on purpose: it selects the carcass. A 600mm unit is
   * 600HL — changing the width means picking a different SJB Code from search,
   * not editing a field, so it is rendered read-only.
   *
   * Height and depth do not feed the price or the recipe lookup. Whatever is
   * typed, the line still prices from the SJB Code exactly as resolved. A
   * value that differs from standard is recorded against the line so it
   * reaches the order — "site needs 715mm not 720mm" — and nothing more.
   */
  function dimensionNote(): string {
    const std = standardDims();
    const parts: string[] = [];
    const w = Number(st.width) || 0;
    const h = Number(st.height) || 0;
    const d = Number(st.depth) || 0;
    if (std.w && w && w !== std.w) parts.push(`width ${w}mm (standard ${std.w}mm)`);
    if (std.h && h && h !== std.h) parts.push(`height ${h}mm (standard ${std.h}mm)`);
    if (std.d && d && d !== std.d) parts.push(`depth ${d}mm (standard ${std.d}mm)`);
    return parts.length ? `CUT TO SIZE: ${parts.join(", ")}` : "";
  }

  /** Beyond this the deviation is flagged louder — it is still allowed, since
   *  refusing a legitimate site measurement would be worse than a warning.
   *  No tolerance was specified, so this is a prompt to double-check, not a
   *  limit. */
  const DIM_TOLERANCE_MM = 50;

  /**
   * The "CUT TO SIZE" line, and the ONLY thing width / height / depth change
   * on this screen.
   *
   * It lives in its own renderer so the three dimension fields can update it
   * without repainting the picker — see the change handlers in wire() for why
   * that matters. Rendered into a slot that is always present, so appearing
   * and disappearing is an innerHTML change rather than a structural one.
   */
  function dimNoteHtml(): string {
    const devNote = dimensionNote();
    if (!devNote) return "";
    const loud = dimensionOutOfTolerance();
    return `<p class="${loud ? "sjb-picker__warn" : "sjb-picker__note"}" data-role="dim-note">${
      escapeHtml(devNote)}${
      loud ? ` — more than ${DIM_TOLERANCE_MM}mm off standard, worth double-checking.` : ""}</p>`;
  }

  function repaintDimNote(): void {
    const slot = host.querySelector<HTMLElement>("[data-role='dim-note-slot']");
    if (slot) slot.innerHTML = dimNoteHtml();
  }
  function dimensionOutOfTolerance(): boolean {
    const std = standardDims();
    const h = Number(st.height) || 0;
    const d = Number(st.depth) || 0;
    return (!!std.h && !!h && Math.abs(h - std.h) > DIM_TOLERANCE_MM)
        || (!!std.d && !!d && Math.abs(d - std.d) > DIM_TOLERANCE_MM);
  }

  /** Suggested row first, then the catalogue's own order — the full list stays
   *  browsable, it just does not make the retailer hunt for the obvious one.
   *  Scoped to the leaf, so a drawer front's list opens on 140mm fronts and a
   *  curved leaf's on curved doors. */
  /**
   * The sizes offered for a leaf: the suggestion first, then everything else
   * IN SIZE ORDER.
   *
   * The list used to arrive in whatever order the server produced, which for
   * this endpoint is `product_code` (catalogue.js) — near enough random once
   * read as millimetres. Steve, 2026-09-10: "the door sizes are not in size
   * order." Sorted by HEIGHT then WIDTH because that is the order the option
   * itself is written in ("715 × 596mm"), so the numbers on screen ascend in
   * the order they are read.
   *
   * THE SUGGESTION STAYS PINNED AT THE TOP rather than sorting into place. It
   * is the size the recipe's own leaf calls for, it is badged as such, and on
   * a long range it is the only entry most cabinets ever need — burying it
   * mid-list to satisfy a pure sort would trade the useful ordering for a
   * tidy one. It is filtered out of the tail so it cannot appear twice.
   *
   * A curved door carries w = 0 BY DESIGN (catalogue.js sets it for anything
   * named "curved" — a curved door has no single flat width), so it sorts to
   * the front of its height band. That is stable and intended, not a zero
   * standing in for missing data.
   */
  function sortedSizesFor(leaf: FrontalLeaf): DoorSize[] {
    const byHeightThenWidth = (a: DoorSize, b: DoorSize): number =>
      (Number(a.h) || 0) - (Number(b.h) || 0)
      || (Number(a.w) || 0) - (Number(b.w) || 0)
      // Same h and w on two SKUs (a range carrying the same size in two
      // finishes): fall back to the code so the order is stable across
      // repaints rather than dependent on sort implementation.
      || String(a.sku).localeCompare(String(b.sku));
    const pool = [...sizesFor(leaf)].sort(byHeightThenWidth);
    const sug = suggestedSizeFor(leaf);
    if (!sug) return pool;
    return [sug.size, ...pool.filter((z) => z.sku !== sug.size.sku)];
  }

  function unitPrice(): number {
    return (priced?.tradePrice ?? 0) + frontalUnitPrice() + drillingUnitPrice();
  }

  /** Price of ONE leaf at its selected size, after the door supplier's
   *  discount. */
  function leafPrice(leaf: FrontalLeaf): number {
    const size = leaf.size;
    if (!size) return 0;
    const discPct = resolveDiscPct(st.frontalLabel, size.category ?? null, st.frontalRange, {
      discountCode: size.discountCode ?? null, doorColour: st.frontalColour || null, code: size.sku,
    }) ?? 0;
    return (Number(size.price) || 0) * (1 - discPct / 100);
  }

  /**
   * The blanking panel side is REQUIRED on the 104 corner units that offer
   * one, Steve's call 2026-09-07: "an unset panel side reaching a real order
   * isn't acceptable."
   *
   * Deliberately a blocker rather than a default. Defaulting to Left would
   * make half of them silently wrong and nothing downstream would ever
   * question it — the note would read "Blanking panel: Left" with the same
   * confidence whether a human chose it or not.
   *
   * Only ever true where the API SAID the unit offers one. A missing field
   * (an older API) must not block the quote — it is not evidence that a side
   * is needed.
   */
  function blankingPanelMissing(): boolean {
    return priced?.blankingPanel?.offered === true && !st.blankingPanel;
  }

  /** Leaves with a size actually chosen — the ones that will be ordered. */
  const pricedLeaves = (): FrontalLeaf[] =>
    st.frontalOn ? frontalLeaves().filter((l) => l.size) : [];

  /** Frontal cost for the whole cabinet — every leaf at its OWN price.
   *
   *  Was `one door's price x door count`, which broke twice: it charged the
   *  same price for a 396 and a 596 on an asymmetric unit, and it charged
   *  nothing at all for the 325 drawer fronts across 134 SKUs that the
   *  frontal never knew existed. */
  function frontalUnitPrice(): number {
    return pricedLeaves().reduce((n, l) => n + leafPrice(l), 0);
  }

  function drillingUnitPrice(): number {
    if (!st.drilling || !st.frontalOn) return 0;
    const perHole = resolveDrillingCharge(st.frontalSlug) ?? 0;
    return perHole > 0 ? Math.round(perHole * totalHoles() * 100) / 100 : 0;
  }

  // ── render ──────────────────────────────────────────────────────────────

  /** DTN's toggle-row markup (cabinet-picker.ts:544-550) so the two pickers'
   *  controls are the same component, not lookalikes. */
  function toggleRow(role: string, value: string, opts: Array<[string, string]>): string {
    return `<div class="qb-w8-toggle" role="group" data-role="${escapeHtml(role)}">${
      opts.map(([v, l]) =>
        `<button type="button" class="${v === value ? "active" : ""}"
                 data-val="${escapeHtml(v)}" aria-pressed="${v === value}">${escapeHtml(l)}</button>`).join("")
    }</div>`;
  }

  /**
   * What the Range select says while it has nothing to offer.
   *
   * IT MUST NOT NAME ANOTHER CONTROL. This option used to read "Pick a
   * catalogue", which is an instruction, sitting inside a DISABLED select,
   * directly under the unlabelled control it was instructing you to use. A
   * disabled select does not open and throws nothing, so clicking it — which
   * is what the wording invites — produces silence and no console error.
   * That cost a bug report on 2026-09-10.
   *
   * Each of the three empty states now says which one it is, and none of
   * them tells the retailer to click this select:
   *   no catalogue chosen  -> points UP, at the labelled row above
   *   fetching             -> says so, rather than guessing at the outcome
   *   fetched and empty    -> a fact about the catalogue, only once true
   */
  function rangePlaceholder(): string {
    if (ranges.length) return "— Select —";
    if (!st.frontalSlug) return "Choose a Catalogue above first";
    if (rangesLoading) return "Loading ranges…";
    return `No ranges available in ${st.frontalLabel || st.frontalSlug.toUpperCase()}`;
  }

  /**
   * A not-priced per-unit side choice: Reduced Gable (column 7) or Blanking
   * Panel Side (column 9). Same markup as Hinge Side, because they are the
   * same kind of decision and sit directly above it.
   *
   * THE THREE STATES ARE DISTINCT AND ONLY ONE OF THEM RENDERS:
   *   offered true      -> the control
   *   offered false     -> nothing. 433 units take no reduced gable and 412
   *                        take no blanking panel; saying so on every one is
   *                        noise, and there is nothing to decide.
   *   field absent      -> nothing, and NO claim either way. An API older
   *                        than 2026-09-07 sends neither field, and the one
   *                        thing that must not happen is a screen stating
   *                        something about the cabinet that nobody checked —
   *                        the same fault that once made every unit read
   *                        "Hinge side is fixed on this unit".
   */
  function sideChoiceRow(o: {
    role: string;
    label: string;
    choice: SjbSideChoice | undefined;
    value: string;
    opts: Array<[string, string]>;
    hint: string;
    warnWhenUnset?: string;
  }): string {
    if (o.choice?.offered !== true || o.opts.length === 0) return "";
    const unset = !o.value;
    return `<div class="qb-w8-row" data-role="${escapeHtml(o.role)}-row">
      <div><label class="qb-w8-lbl">${escapeHtml(o.label)}</label>
        ${toggleRow(o.role, o.value, o.opts)}
        ${unset && o.warnWhenUnset
          ? `<p class="sjb-picker__warn" data-role="${escapeHtml(o.role)}-unset">${escapeHtml(o.warnWhenUnset)}</p>`
          : `<p class="sjb-picker__hint">${escapeHtml(o.hint)}</p>`}
      </div></div>`;
  }

  /** Full repaint, wrapped so the LEFT pane keeps its scroll position and the
   *  field just used keeps focus. This picker is the one the problem was
   *  reported against — a four-door larder's Frontal section is a long way
   *  down — but the wrapper goes on all three pickers, because they all
   *  replace their host wholesale. See @tkos/shared-ui repaint-preserving-scroll.ts. */
  function paint(): void {
    if (!mounted) return;
    repaintPreservingScroll(host, paintNow);
  }

  function paintNow(): void {
    if (!mounted) return;
    // Idempotent via drillSeededFor — reseeds only when the door count or
    // height actually changed, so a size change reaches the blocks without
    // discarding hand-entered positions on every repaint.
    seedDrillSpecs();
    if (combo) { combo.unmount(); combo = null; }
    if (edgingCombo) { edgingCombo.unmount(); edgingCombo = null; }

    const cab = priced;
    const hc = cab?.handingChoice;
    const zones = store.get().zones;

    const cabinetTrade = cab?.tradePrice ?? 0;
    const unit = unitPrice();
    const std = standardDims();

    const zoneOpts = zones.map((z) =>
      `<option value="${escapeHtml(z.id)}"${z.id === st.zoneId ? " selected" : ""}>${escapeHtml(z.name)}</option>`).join("");

    // Layout mirrors the DTN/W8 picker (cabinet-picker.ts:499-550) and reuses
    // its primitives — qb-w8-summary / -row / -lbl / -inp / -toggle — rather
    // than a parallel set. Two cabinet configurators sit in the same pane and
    // a retailer moving between suppliers should not have to relearn where
    // things are. Only the accent is re-tinted, scoped under .sjb-picker.
    host.innerHTML = `
      <div class="sjb-picker" data-role="sjb-picker" data-code="${escapeHtml(sjbCode)}">
        <header class="sjb-picker__head">
          <div>
            <h3 class="sjb-picker__title"><span class="sup">SJB</span>${escapeHtml(cab?.desc || sjbCode)}</h3>
            <p class="sjb-picker__sub">${escapeHtml(sjbCode)}${cab?.dims ? ` · ${escapeHtml(cab.dims)}` : ""}</p>
          </div>
          <button type="button" class="sjb-picker__close" data-role="cancel" aria-label="Cancel">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                 stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </header>

        ${st.error ? `<p class="sjb-picker__error" role="alert">${escapeHtml(st.error)}</p>` : ""}

        <div class="qb-w8-summary" data-role="unit-price">
          <div>
            <div class="lbl">Unit price${cab?.carcass.discountLabel ? ` · ${escapeHtml(cab.carcass.discountLabel)}` : ""}</div>
            <div class="val">${cab ? fmtGbp(unit) : "—"}</div>
          </div>
          <div class="right">
            <span class="qb-w8-summary-mult">× ${st.qty}</span>
            <span class="qb-w8-summary-total">${cab ? fmtGbp(unit * (st.qty || 1)) : "—"}</span>
          </div>
        </div>

        <div class="qb-w8-row two">
          <div><label class="qb-w8-lbl">Zone</label>
            <select class="qb-w8-inp" data-role="zone">${zoneOpts}</select></div>
          <div><label class="qb-w8-lbl">Qty</label>
            <input class="qb-w8-inp" type="number" min="1" step="1" value="${st.qty}" data-role="qty" /></div>
        </div>

        <div class="qb-w8-row three">
          <div>
            <label class="qb-w8-lbl">Width (mm)</label>
            <input class="qb-w8-inp" type="number" value="${escapeHtml(st.width)}" data-role="width"
                   max="${std.w || ""}" step="1"
                   title="Scribe down only — a wider unit is a different SJB Code" />
          </div>
          <div><label class="qb-w8-lbl">Height (mm)</label>
            <input class="qb-w8-inp" type="number" value="${escapeHtml(st.height)}" data-role="height" /></div>
          <div><label class="qb-w8-lbl">Depth (mm)</label>
            <input class="qb-w8-inp" type="number" value="${escapeHtml(st.depth)}" data-role="depth" /></div>
        </div>
        <p class="sjb-picker__hint">Width can be scribed DOWN from the SJB Code's ${std.w || "standard"}mm,
          never up — a wider unit is a different code. Width, height and depth are a manufacturing
          note only; none of them change the price or the carcass.</p>
        <div data-role="dim-note-slot">${dimNoteHtml()}</div>

        <section class="sjb-picker__sec">
          <div data-role="colour-slot"></div>
          <p class="sjb-picker__hint">Inherited from the quote default. Changing it here affects this cabinet only.</p>
        </section>

        <section class="sjb-picker__sec">
          <div class="qb-w8-row two">
            <div><label class="qb-w8-lbl">Hinge finish</label>
              ${toggleRow("hinge", st.hingeFinish, [["standard", "Standard"], ["onyx", "Onyx"]])}</div>
            <div><label class="qb-w8-lbl">Drawer system</label>
              ${toggleRow("drawer", st.drawerSystem, [["antaro", "Antaro"], ["legrabox", "Legrabox"]])}</div>
          </div>
          <div class="qb-w8-row two">
            <div><div data-role="edging-slot"></div>
              <p class="sjb-picker__hint">Same decors as the carcass — every one SJB stock has matching
                tape. No discount shown: edge tape costs the same whatever the decor.
                Inherited from the quote default; this cabinet only.</p>
            </div>
            <div><label class="qb-w8-lbl">Notes</label>
              <input class="qb-w8-inp" type="text" value="${escapeHtml(st.notes)}"
                     data-role="notes" placeholder="Manufacturing or site note" />
              <p class="sjb-picker__hint">${priced?.notes
                ? `Pre-filled from SJB's spec for ${escapeHtml(sjbCode)} — edit freely, it rides on the cabinet line.`
                : "Rides on the cabinet line and reaches the order."}</p>
            </div>
          </div>
          ${sideChoiceRow({
              role: "reduced-gable",
              label: "Reduced gable",
              choice: cab?.reducedGable,
              value: st.reducedGable,
              // "None" first and selected by default. A reduced gable is the
              // exception on these units, not the rule, so the neutral answer
              // has to be the resting state.
              opts: [["", "None"], ...(cab?.reducedGable?.options ?? []).map((o) => [o, o] as [string, string])],
              hint: cab?.reducedGable?.source === "category"
                ? "Offered because of this unit's category — SJB's file does not specify it for this SKU."
                : "From SJB's spec for this SKU. A manufacturing note only — it does not change the price.",
            })}
          ${sideChoiceRow({
              role: "blanking-panel",
              label: "Blanking panel side",
              choice: cab?.blankingPanel,
              value: st.blankingPanel,
              // NO "None": a corner unit's return is blanked either way, so
              // the only question is which side. Unset stays unset and says
              // so, rather than resolving itself to Left.
              opts: (cab?.blankingPanel?.options ?? []).map((o) => [o, o] as [string, string]),
              hint: "Which side the blanked return faces. A manufacturing note only — it does not change the price.",
              warnWhenUnset: "Required — this unit cannot be added until a side is chosen.",
            })}
          ${hc?.choice === true
            ? `<div class="qb-w8-row"><div><label class="qb-w8-lbl">Hinge side</label>
                 ${toggleRow("handing", st.handing, HANDING_OPTIONS.map((o) => [o, o] as [string, string]))}</div></div>`
            : hc?.choice === false
              // Only claim "fixed" when the server actually SAID so. An earlier
              // cut used `hc?.choice` truthiness, so a response without a
              // handingChoice at all — an API older than the field — rendered
              // every cabinet as fixed, including single doors that plainly
              // are not. Absent data must not read as a positive statement.
              ? `<p class="sjb-picker__fixed" data-role="handing-fixed">Hinge side is fixed on this unit${
                   hc.source === "fixed_double" ? " — both doors hinge outward" : ""}.</p>`
              : cab
                ? `<p class="sjb-picker__warn" data-role="handing-unknown" role="alert">
                     Hinge side unavailable — the catalogue service did not return a
                     handing spec for this cabinet.
                   </p>`
                : ""}
        </section>

        <section class="sjb-picker__sec">
          <h4 class="sjb-picker__sec-title">Frontal</h4>
          <label class="sjb-picker__check" data-role="frontal-on-row">
            <input type="checkbox" data-role="frontal-on"${st.frontalOn ? " checked" : ""} />
            <span>Include doors</span>
          </label>
          ${!st.frontalOn
            ? `<p class="sjb-picker__hint" data-role="frontal-off">
                 Cabinet only — no doors, drawer fronts or drilling will be added to the quote.
                 Remembered for this quote, so the next SJB cabinet starts the same way.
               </p>`
            : frontalSuppliers.length === 0
            // Two very different situations that used to print the same
            // sentence: nothing is enabled, versus the request never landed.
            // The second is a fault to chase, not a fact about the catalogue.
            ? (st.frontalLoadFailed
                ? `<p class="sjb-picker__warn" data-role="frontal-error" role="alert">
                     Could not load the frontal catalogues. The catalogue service may be
                     out of date — frontals cannot be added until it responds.
                   </p>`
                : `<p class="sjb-picker__hint" data-role="frontal-empty">No door catalogue has been enabled for frontals yet.</p>`)
            // A SELECT, not a pill row, and labelled.
            //
            // It was an unlabelled pill row until 2026-09-10, which produced
            // the "Catalogue dropdown won't open" report: with one catalogue
            // enabled it was preselected and nobody had to find it, so the
            // missing label went unnoticed for months. Adding PWS made it the
            // first thing a retailer must answer, and an unlabelled pill row
            // above a select reading "Pick a catalogue" sent them to the
            // select — disabled until this control is answered, so it
            // silently would not open.
            //
            // Pills do not scale here either. Steve's call, 2026-09-10: the
            // row already carries TKC and PWS, Uform is coming, and any
            // catalogue registered in SJB_FRONTAL_SUPPLIERS lands in it. A
            // select stays one line at any length, and matches Range and
            // Colour directly beneath it — three ordered choices that read as
            // one sequence rather than one odd control and two normal ones.
            : `<div class="qb-w8-row">
                 <div><label class="qb-w8-lbl">Catalogue${
                     st.frontalSlug ? " (quote default)" : ""}</label>
                   <select class="qb-w8-inp" data-role="frontal-supplier">
                     <option value=""${st.frontalSlug ? "" : " selected"}>${
                       frontalSuppliers.length === 1 ? "— Select —" : "— Select a catalogue —"}</option>
                     ${frontalSuppliers.map((f) =>
                       `<option value="${escapeHtml(f.slug)}"${
                         f.slug === st.frontalSlug ? " selected" : ""}>${escapeHtml(f.label)}</option>`).join("")}
                   </select>
                   <p class="sjb-picker__hint">Remembered for this quote — the next SJB cabinet opens on it.</p>
                 </div>
               </div>
               <label class="sjb-picker__check" data-role="remember-frontal-row">
                 <input type="checkbox" data-role="remember-frontal"${st.rememberFrontal ? " checked" : ""} />
                 <span>Remember range &amp; colour for this quote${
                   st.frontalRange ? ` — currently ${escapeHtml(st.frontalRange)}${
                     st.frontalColour ? ` · ${escapeHtml(st.frontalColour)}` : ""}` : ""}</span>
               </label>
               <div class="qb-w8-row two">
                 <div><label class="qb-w8-lbl">Range</label>
                   <select class="qb-w8-inp" data-role="frontal-range"${ranges.length ? "" : " disabled"}>
                     <option value="">${escapeHtml(rangePlaceholder())}</option>
                     ${ranges.map((r) => `<option value="${escapeHtml(r.name)}"${r.name === st.frontalRange ? " selected" : ""}>${escapeHtml(r.name)}</option>`).join("")}
                   </select></div>
                 <div><label class="qb-w8-lbl">Colour</label>
                   ${currentRange()
                     ? renderColourSelect({
                         groups: colourGroups(),
                         selectedTier: st.frontalTier,
                         selectedColour: st.frontalColour,
                         role: "frontal-colour",
                         className: "qb-w8-inp",
                         emptyText: "No colours uploaded for this range yet",
                       })
                     : `<select class="qb-w8-inp" data-role="frontal-colour" disabled><option>Pick a range</option></select>`}
                   ${st.frontalColour ? renderTierBadge(st.frontalColour, st.frontalTier || "stock") : ""}</div>
               </div>

               <div class="sjb-picker__leaves" data-role="leaves">
                 ${
                   // ONE ROW PER LEAF. A cabinet is a list of leaves, not a
                   // single size: this is where a 1000mm unit gets a 396
                   // beside a 596, a drawerline gets its 140mm front above
                   // its 570mm door, and a curved base gets a curved leaf
                   // beside a flat one — each with its own suggestion, its
                   // own list and its own price.
                   frontalLeaves().map((leaf) => {
                    const pool = sortedSizesFor(leaf);
                    const sug = suggestedSizeFor(leaf);
                    const chosen = st.leafSkus[leaf.index] ?? "";
                    return `
                    <div class="sjb-picker__leaf" data-role="leaf" data-leaf="${leaf.index}">
                      <div class="sjb-picker__leaf-head">
                        <span class="sjb-picker__leaf-name">${escapeHtml(leafName(leaf))}</span>
                        <span class="sjb-picker__leaf-meta">recipe ${escapeHtml(dimsLabel(leaf.recipeH, leaf.recipeW, leaf.curved))}</span>
                        <span class="sjb-picker__leaf-price">${leaf.size ? escapeHtml(fmtGbp(leafPrice(leaf))) : "—"}</span>
                      </div>
                      <select class="qb-w8-inp" data-role="leaf-size" data-leaf="${leaf.index}"${pool.length ? "" : " disabled"}>
                        <option value="">${pool.length
                          ? "— Select —"
                          : (leaf.curved
                              ? "No curved door in this range"
                              : (sizes.length ? "No matching size" : "Pick a range"))}</option>
                        ${pool.map((z) => {
                           const isSug = sug?.size.sku === z.sku;
                           const dims = z.type === "curved" ? `${z.h}mm Curved` : `${z.h} × ${z.w}mm`;
                           return `<option value="${escapeHtml(z.sku)}"${z.sku === chosen ? " selected" : ""}>${
                             escapeHtml(`${isSug ? (sug?.exact ? "★ " : "≈ ") : ""}${dims}${z.label ? ` ${z.label}` : ""} — ${fmtGbp(z.price)}`)}</option>`;
                         }).join("")}
                      </select>
                      ${sug ? `<p class="sjb-picker__hint" data-role="size-suggestion">${escapeHtml(
                        (sug.exact
                          ? (sug.delta === 0
                              ? `★ matches this cabinet's ${dimsLabel(leaf.recipeH, leaf.recipeW, leaf.curved)} ${leafNoun(leaf)}`
                              : `★ within ${SIZE_TOLERANCE_MM}mm of this cabinet's ${dimsLabel(leaf.recipeH, leaf.recipeW, leaf.curved)} ${leafNoun(leaf)}`)
                          : `≈ nearest to this cabinet's ${dimsLabel(leaf.recipeH, leaf.recipeW, leaf.curved)} ${leafNoun(leaf)} — a suggestion, not a lock`)
                        // A 570mm door on a 720mm carcass reads like an error beside
                        // the 715mm full-height doors elsewhere in the range. It is
                        // not — it is the door under the drawer front.
                        + (isPartHeightDoor(leaf)
                            ? ` — part-height door beneath the drawer front, not a full-height ${(standardDims().h || 720) - 5}mm door`
                            : ""))}</p>`
                        : (leaf.curved
                            ? `<p class="sjb-picker__warn" data-role="no-curved" role="alert">This range does not stock a curved door. Pick another range, or order this leaf separately.</p>`
                            : "")}
                    </div>`;
                  }).join("")}
               </div>
               ${/*
                  * GATED ON EITHER SOURCE OF HOLES. It used to be `drillSpecs.length`
                  * alone, and drillSpecs is indexed by DOOR — so a bridging unit,
                  * whose only frontal is a sub-400mm flap, had no doors, no specs and
                  * therefore no drilling control at all. Steve, 2026-09-10: there was
                  * "no option to include drilling for the door on a bridging unit".
                  * The per-door blocks below stay keyed off drillSpecs, correctly:
                  * a top-hung flap has no side to choose and no positions to edit.
                  */ ""}
               ${drillSpecs.length || topHungHoles() > 0 ? `
               <label class="sjb-picker__check">
                 <input type="checkbox" data-role="drilling"${st.drilling ? " checked" : ""} />
                 <span>Charge hinge drilling — ${totalHoles()} hole${totalHoles() === 1 ? "" : "s"}${
                   topHungHoles() > 0 && !drillSpecs.length ? " in the top of the unit" : ""}${
                   drillingUnitPrice() > 0 ? ` (${fmtGbp(drillingUnitPrice())})` : " (no charge set)"}</span>
               </label>
               ${doorLeaves().map((leaf) => {
                   const auto = drillingForHeight(leaf.h);
                   const n = doorLeaves().length;
                   const dims = dimsLabel(leaf.h, leaf.w, leaf.curved);
                   return renderDrillingBlock(drillSpecs[leaf.doorIndex] ?? blankDrillingSpec(), {
                     id: `door-${leaf.doorIndex}`,
                     title: n > 1
                       ? `${leafName(leaf)} — ${dims}`
                       : `Drilling & Handing — ${dims}`,
                     // Described in the SAME terms as the Top/Bottom inputs
                     // directly beneath it. This hint used to read "holes at
                     // 98, 617mm" while the fields under it asked for a top
                     // and a bottom distance — the inconsistency Steve
                     // reported, in its most confusing form.
                     // The pre-fill comes from the door HEIGHT, so it only
                     // describes a vertical run. On a top-hung selection it
                     // would print height-derived numbers under left/right
                     // labels — the same hint-vs-fields inconsistency that
                     // started all of this — so it is withheld instead.
                     note: (drillSpecs[leaf.doorIndex]?.side === "T")
                       ? "Top-hung: the hinges run along the top edge, so there is no "
                         + "height-based pre-fill. Enter the left and right distances below."
                       : `Pre-filled from the ${leaf.fromSize ? "selected size" : "door height"}: ${
                         holePositionText({
                           topMm: auto.positions[0] ?? 0,
                           bottomMm: leaf.h - (auto.positions[auto.positions.length - 1] ?? 0),
                           midMm: auto.positions.slice(1, -1),
                           holeQty: auto.holes,
                           side: drillSpecs[leaf.doorIndex]?.side ?? "L",
                         })}. Adjust below if needed.`,
                   });
                 }).join("")}
               <ul class="sjb-picker__drill" data-role="drill-spec">
                 ${drillingRows().map((r) => `<li>${escapeHtml(r)}</li>`).join("")}
               </ul>` : ""}
               ${st.frontalRange ? renderExtrasSection({
                   extras, open: extrasOpen, loading: extrasLoading, qtyById: extrasQty,
                 }) : ""}
               ${priced?.drilling?.hingeQtyMatchesSpec === false ? `
               <p class="sjb-picker__warn" role="alert" data-role="drill-warning">
                 Check before ordering: the door heights call for ${priced.drilling.specHoles}
                 hinges, but this cabinet is priced with ${priced.drilling.recipeHingeQty}.
                 Drilling below follows the door heights.
               </p>` : ""}`}
        </section>

        <section class="sjb-picker__totals">
          ${cab ? `
            <div class="sjb-picker__row"><span>Cabinet${cab.carcass.discountLabel ? ` · ${escapeHtml(cab.carcass.discountLabel)}` : ""}</span><span>${fmtGbp(cabinetTrade)}</span></div>
            <div class="sjb-picker__row sjb-picker__row--sub"><span>carcass ${fmtGbp(cab.carcass.price)} + hardware ${fmtGbp(cab.hardwareTotal)} at list</span><span></span></div>
          ` : `<div class="sjb-picker__row"><span>Cabinet</span><span>${st.boardKey ? "…" : "select a board colour"}</span></div>`}
          ${pricedLeaves().length ? `<div class="sjb-picker__row"><span>Frontal · ${escapeHtml(st.frontalRange)} ${escapeHtml(st.frontalColour)}${
            pricedLeaves().length > 1 ? ` · ${pricedLeaves().length} leaves` : ""}</span><span>${fmtGbp(frontalUnitPrice())}</span></div>`
            : (st.frontalOn ? "" : `<div class="sjb-picker__row sjb-picker__row--sub"><span>Cabinet only — no frontals</span><span></span></div>`)}
          ${pricedLeaves().length > 1 ? pricedLeaves().map((l) => `<div class="sjb-picker__row sjb-picker__row--sub"><span>${
            escapeHtml(`${leafName(l)} · ${dimsLabel(l.h, l.w, l.curved)}`)}</span><span>${fmtGbp(leafPrice(l))}</span></div>`).join("") : ""}
          ${drillingUnitPrice() > 0 ? `<div class="sjb-picker__row"><span>Drilling</span><span>${fmtGbp(drillingUnitPrice())}</span></div>` : ""}
          <div class="sjb-picker__row sjb-picker__row--total"><span>Trade${st.qty > 1 ? ` × ${st.qty}` : ""}</span><span>${fmtGbp(unit * (st.qty || 1))}</span></div>
        </section>

        <footer class="sjb-picker__foot">
          <button type="button" class="qb-btn" data-role="cancel-2">Cancel</button>
          <button type="button" class="qb-btn qb-btn--primary" data-role="confirm"
                  ${st.busy || !cab || cab.blocked.length > 0 || blankingPanelMissing() ? "disabled" : ""}>
            ${st.busy ? "Pricing…"
              : blankingPanelMissing() ? "Choose a blanking panel side"
              : "Add to quote"}
          </button>
        </footer>
      </div>`;

    const eSlot = host.querySelector<HTMLElement>("[data-role='edging-slot']");
    if (eSlot) {
      edgingCombo = mountColourCombobox(eSlot, {
        colours,
        selectedKey: edgingKeyForLabel(st.edgingColour, colours),
        label: "Front edging colour",
        role: "sjb-edging-line",
        // 614 decors, so it needs the filter box the plain <select> never had
        // — but flat: edging carries none of the board's pricing.
        flat: true,
        placeholder: SJB_EDGING_MATCHING,
        pinned: { key: SJB_EDGING_MATCHING, label: SJB_EDGING_MATCHING },
        onPick: (c) => {
          st.edgingColour = c.key === SJB_EDGING_MATCHING ? SJB_EDGING_MATCHING : c.label;
          paint();
        },
      });
    }
    const slot = host.querySelector<HTMLElement>("[data-role='colour-slot']");
    if (slot) {
      combo = mountColourCombobox(slot, {
        colours,
        selectedKey: st.boardKey,
        label: "Board colour (this cabinet)",
        role: "sjb-picker-colour",
        onPick: (c) => {
          st.boardKey = c.key; st.boardLabel = c.label;
          void reprice();
        },
      });
    }
    wire();
  }

  function seg(role: string, fn: (v: string) => void): void {
    host.querySelectorAll<HTMLElement>(`[data-role='${role}'] button`).forEach((b) => {
      b.addEventListener("click", () => { const v = b.dataset["val"]; if (v !== undefined) fn(v); });
    });
  }
  const sel = (role: string, fn: (v: string) => void): void => {
    host.querySelector<HTMLSelectElement>(`[data-role='${role}']`)
      ?.addEventListener("change", (e) => fn((e.target as HTMLSelectElement).value));
  };

  function wire(): void {
    seg("hinge", (v) => { st.hingeFinish = v === "onyx" ? "onyx" : "standard"; void reprice(); });
    seg("drawer", (v) => { st.drawerSystem = v === "legrabox" ? "legrabox" : "antaro"; void reprice(); });
    seg("handing", (v) => { st.handing = v; paint(); });
    seg("reduced-gable", (v) => { st.reducedGable = v; paint(); });
    seg("blanking-panel", (v) => { st.blankingPanel = v; paint(); });
    // Cabinets-only is a property of the JOB. Written straight back to the
    // quote default so the next SJB cabinet on this quote starts the same
    // way — 30 identical toggles is how a kitchen ships with no fronts.
    host.querySelector<HTMLInputElement>("[data-role='remember-frontal']")
      ?.addEventListener("change", (e) => {
        st.rememberFrontal = (e.target as HTMLInputElement).checked;
        setSjbDefaults(store, { rememberFrontal: st.rememberFrontal });
        // Switching it ON adopts what is on screen right now, rather than
        // waiting for the retailer to re-pick something they have already
        // chosen.
        rememberFrontal();
        paint();
      });
    host.querySelector<HTMLInputElement>("[data-role='frontal-on']")
      ?.addEventListener("change", (e) => {
        st.frontalOn = (e.target as HTMLInputElement).checked;
        setSjbDefaults(store, { frontalOn: st.frontalOn });
        paint();
      });
    sel("frontal-supplier", (v) => {
      st.frontalSlug = v;
      st.frontalLabel = frontalSuppliers.find((s) => s.slug === v)?.label ?? v.toUpperCase();
      st.frontalRange = ""; st.frontalColour = ""; st.leafSkus = {}; sizes = [];
      // Written to the quote UNCONDITIONALLY, unlike range and colour which
      // go through rememberFrontal(). The catalogue is a property of the job,
      // not of one cabinet — see SjbDefaults.frontalSlug.
      setSjbDefaults(store, { frontalSlug: v });
      if (!v) { ranges = []; paint(); return; }
      void loadRanges(v);
    });
    sel("frontal-range", (v) => {
      st.frontalRange = v; st.frontalColour = ""; st.leafSkus = {}; sizes = [];
      rememberFrontal();
      paint();
    });
    sel("frontal-colour", (v) => {
      // Shared select encodes "tier|colour" so one handler recovers both.
      const [tier, ...rest] = v.split("|");
      st.frontalTier = (rest.length ? tier : "") as DoorType | "";
      st.frontalColour = rest.length ? rest.join("|") : v;
      st.leafSkus = {};
      rememberFrontal();
      void loadSizes();
    });
    // One select per leaf, each scoped by its recipe index so choosing a
    // door for leaf 2 cannot move leaf 1.
    host.querySelectorAll<HTMLSelectElement>("[data-role='leaf-size']").forEach((el) => {
      el.addEventListener("change", () => {
        const i = Number(el.dataset["leaf"]);
        if (!Number.isFinite(i)) return;
        if (el.value) st.leafSkus[i] = el.value;
        else delete st.leafSkus[i];
        paint();
      });
    });

    // No repaint per keystroke — that would destroy the input being typed
    // into and put the caret at 0, which is exactly the bug the colour filter
    // hit (see the module header on sjb-colour-combobox).
    host.querySelector<HTMLInputElement>("[data-role='notes']")
      ?.addEventListener("input", (e) => { st.notes = (e.target as HTMLInputElement).value; });
    sel("zone", (v) => { st.zoneId = v; });
    host.querySelector<HTMLInputElement>("[data-role='qty']")
      ?.addEventListener("input", (e) => {
        st.qty = Math.max(1, Number((e.target as HTMLInputElement).value) || 1); paint();
      });
    // ── Width / height / depth ──────────────────────────────────────────
    //
    // THESE MUST NOT REPAINT THE PICKER. `change` on a text field fires on
    // BLUR, and the blur that matters is the one caused by the mousedown that
    // begins a click on something else. A full repaint there replaces the
    // control the pointer is already down on, mouseup lands on its
    // replacement, and the browser fires NO click event at all — the click is
    // swallowed silently.
    //
    // Reported by Steve 2026-09-16 against a 900mm corner base: choosing a
    // blanking panel side "highlights but the Required warning stays and Add
    // stays disabled". The highlight was the button's :hover state; the click
    // never reached the handler, so the side was never set. It looked
    // specific to that control because that control is the only one whose
    // click is GATED — everywhere else a swallowed first click is just a
    // click you make again without noticing.
    //
    // Nothing about these three needs a repaint anyway: they are a
    // manufacturing note, they do not reprice (the carcass is fixed by the
    // SJB Code), and the only thing on screen that reads them is the CUT TO
    // SIZE line. So they patch that line and leave every other node alone.
    host.querySelector<HTMLInputElement>("[data-role='width']")
      ?.addEventListener("change", (e) => {
        const el = e.target as HTMLInputElement;
        const std = standardDims().w;
        const v = Number(el.value) || 0;
        // Clamp rather than reject: a retailer scribing to fit should not have
        // to guess the ceiling, and silently accepting a wider value would
        // describe a unit SJB cannot build from this carcass.
        st.width = std && v > std ? String(std) : el.value.trim();
        st.widthClamped = !!(std && v > std);
        // Write the clamped value back explicitly. The repaint used to do
        // this as a side effect of re-rendering the field from state; with the
        // repaint gone it has to be said, or a clamped width would stay on
        // screen as the number that was typed.
        if (el.value !== st.width) el.value = st.width;
        repaintDimNote();
      });
    for (const role of ["height", "depth"] as const) {
      host.querySelector<HTMLInputElement>(`[data-role='${role}']`)
        ?.addEventListener("change", (e) => {
          st[role] = (e.target as HTMLInputElement).value.trim();
          repaintDimNote();
        });
    }
    host.querySelector<HTMLInputElement>("[data-role='drilling']")
      ?.addEventListener("change", (e) => { st.drilling = (e.target as HTMLInputElement).checked; paint(); });
    // One block per door, each scoped to its own element so two doors cannot
    // drive each other's controls. No `persist`: a hinge side belongs to this
    // door, not to the next quote.
    wireExtrasSection(host, { extras, open: extrasOpen, loading: extrasLoading, qtyById: extrasQty }, {
      onToggle: () => { extrasOpen = !extrasOpen; paint(); },
      onQty: (id, n) => { extrasQty[id] = n; },
      onAdd: (x) => { pendingExtras.push(x); paint(); },
    });
    host.querySelectorAll<HTMLElement>("[data-role='drill-block']").forEach((block) => {
      const idx = Number((block.dataset["drillId"] ?? "").replace("door-", ""));
      const spec = drillSpecs[idx];
      if (!spec) return;
      wireDrillingBlock(block, spec, { onChange: () => paint() });
    });
    host.querySelector<HTMLElement>("[data-role='cancel']")?.addEventListener("click", onDone);
    host.querySelector<HTMLElement>("[data-role='cancel-2']")?.addEventListener("click", onDone);
    host.querySelector<HTMLElement>("[data-role='confirm']")?.addEventListener("click", () => void confirm());
  }

  // ── commit ──────────────────────────────────────────────────────────────

  async function confirm(): Promise<void> {
    if (!priced || st.busy) return;
    if (priced.blocked.length > 0) {
      await alertDialog("This cabinet cannot be priced yet.", { title: "Cannot add" });
      return;
    }
    // Checked here as well as on the button. `disabled` is a rendering state
    // — it can be raced by an Enter key or a stale repaint — and this one is
    // a rule about what may reach an order, so it is enforced where the line
    // is actually built.
    if (blankingPanelMissing()) {
      await alertDialog(
        "This is a corner unit, so one side of it is blanked off. Choose which side "
        + "before adding it — the workshop cannot make it without knowing.",
        { title: "Blanking panel side needed" });
      return;
    }
    const groupId = newId("sjbg");
    const qty = st.qty || 1;
    const zoneId = st.zoneId || store.get().zones[0]?.id || "";
    const markupPct = store.get().defaults.markupPct ?? 0;

    const cabinet = sjbLineToItem(priced, { qty, markupPct, boardLabel: st.boardLabel });
    cabinet.sjbGroupId = groupId;
    cabinet.sjbGroupHead = true;
    if (st.handing) {
      cabinet.hinging = st.handing === "Left" ? "L"
        : st.handing === "Right" ? "R"
        : st.handing === "Both" ? "B" : "N";
      cabinet.notes = `${cabinet.notes} | Hinge side: ${st.handing}`;
    }
    // Manufacturing dimensions. These ride on the line so they reach the
    // order, and are deliberately applied AFTER sjbLineToItem has priced it:
    // the price and the carcass code came from the SJB Code and are not
    // recomputed here. Width is never taken from the form — it is the SJB
    // Code's own, which sjbLineToItem already set.
    const std = standardDims();
    const h = Number(st.height) || 0;
    const d = Number(st.depth) || 0;
    if (h) cabinet.height = h;
    if (d) cabinet.depth = d;
    const w = Number(st.width) || 0;
    cabinet.width = w && std.w ? Math.min(w, std.w) : (std.w || w);
    if (st.edgingColour) {
      cabinet.edging = st.edgingColour;
      cabinet.sjbEdgingColour = st.edgingColour;
      cabinet.notes = `${cabinet.notes} | Front edging: ${st.edgingColour}`;
    }
    // Reduced gable and blanking panel side. Price-neutral by Steve's
    // confirmation (2026-09-07) — SJB's files carry no charge for either — so
    // they are applied AFTER sjbLineToItem has priced the line, exactly like
    // the manufacturing dimensions and the edging above. Neither touches the
    // carcass price, the carcass code or the recipe lookup.
    if (st.reducedGable) {
      cabinet.sjbReducedGable = st.reducedGable;
      cabinet.notes = `${cabinet.notes} | Reduced gable: ${st.reducedGable}`;
    }
    if (st.blankingPanel) {
      cabinet.sjbBlankingPanel = st.blankingPanel;
      cabinet.notes = `${cabinet.notes} | Blanking panel: ${st.blankingPanel}`;
    }
    const devNote = dimensionNote();
    if (devNote) cabinet.notes = `${cabinet.notes} | ${devNote}`;
    // Last, so a hand-typed note reads at the end of the line rather than
    // buried between the board and the edging.
    if (st.notes.trim()) cabinet.notes = `${cabinet.notes} | ${st.notes.trim()}`;

    const lines: QuoteItem[] = [cabinet];

    // ONE LINE PER PHYSICAL LEAF — every door AND every drawer front.
    //
    // Two under-orders converged here. The frontal used to push a single door
    // line however many doors the cabinet had, so all 227 multi-door SKUs
    // were quoted and ordered a door short (three short, on the 20 four-door
    // units). And it only ever knew about doors, so the 325 drawer fronts
    // across 134 SKUs were never ordered at all — a drawerline shipped as a
    // door with a hole where its top front should be.
    //
    // Each leaf's spec goes in the DESCRIPTION as well as the notes: the PO
    // export (portal_orders.js coerceLineItems) keeps exactly
    // {code, description, qty, tradePence, zone} and drops everything else,
    // so a description is the only field that reaches TKC. Notes keep a
    // fuller copy for the internal quote PDF, which does render them — and
    // carry the same hole positions, so the line reads as a complete spec
    // wherever it is looked at.
    const committed = pricedLeaves();
    if (committed.length) {
      committed.forEach((leaf) => {
        const size = leaf.size;
        if (!size) return;
        const discPct = resolveDiscPct(st.frontalLabel, size.category ?? null, st.frontalRange, {
      discountCode: size.discountCode ?? null, doorColour: st.frontalColour || null, code: size.sku,
    }) ?? 0;
        const spec = leaf.kind === "door" ? drillSpecs[leaf.doorIndex] : undefined;
        const sideWord = spec?.side === "R" ? "Right hand"
          : spec?.side === "T" ? "Top hung"
          : "Left hand";
        const unitWord = spec?.unitType === "wall" ? "wall" : "base";
        const base = `${st.frontalRange} ${st.frontalColour} ${
          dimsLabel(leaf.h, leaf.w, leaf.curved)}${size.label ? ` ${size.label}` : ""}`.trim();

        // Position detail belongs on THIS leaf's line — it is the
        // manufacturing spec for this door, and burying it in a shared
        // drilling line makes it belong to neither.
        //
        // Measured against the leaf's EFFECTIVE height, not the recipe's.
        // Reading `priced.drilling.doors[i].height` here meant that picking a
        // different catalogue size moved the positions shown on screen but
        // committed the ones for the door the recipe had assumed.
        // Reads the STORED top and bottom distances rather than the
        // resolved positions. Both are independently editable, so a
        // from-the-top figure hides what the fabricator was actually told.
        let holeText = "";
        if (spec && spec.mode === "hinge") holeText = holePositionText(spec);
        // A top-hung flap has no drillSpec (no side, no editable positions),
        // so without this its line said nothing about holes in the one field
        // that reaches the supplier — the drilling lived only in the notes.
        if (!holeText && leaf.part === "top-hung" && leaf.topHungHoles > 0) {
          holeText = `${leaf.topHungHoles} hole${leaf.topHungHoles === 1 ? "" : "s"} in the top edge`;
        }
        // Named by what the leaf IS. Every `front` used to be committed as a
        // "drawer front" — so a bridging unit's top-hung flap went onto the
        // quote (and the purchase order) as a drawer front, and read as
        // undrilled. leafNoun is what the picker already shows on screen.
        const kindWord = leaf.kind === "front"
          ? leafNoun(leaf)
          : `${sideWord} ${unitWord} door`;
        // The hole clause is NOT repeated in the description (Steve,
        // 2026-10-10): it made every door title run to two lines, and the
        // spec is in the notes below — which the PO now carries too
        // (portal_orders.js coerceLineItems keeps `notes`, po_pdf.js prints
        // them), so the supplier still gets every position.
        const desc = `${base} — ${kindWord}`;

        const line: QuoteItem = {
          id: newId("i"), category: "doors", supplier: st.frontalLabel,
          code: size.sku || `${st.frontalSlug.toUpperCase()}-${leaf.h}x${leaf.w}`,
          desc, qty, width: leaf.w, height: leaf.h, depth: 0,
          // Structured alongside the words, matching door-picker.ts. The line
          // then carries the actual distances rather than only a sentence, so
          // nothing downstream has to parse the note or back-compute a bottom
          // distance from the height.
          ...(spec && spec.mode === "hinge"
            ? { topMm: spec.topMm, bottomMm: spec.bottomMm, doorHoleQty: spec.holeQty,
                doorHoleMidMm: spec.midMm, drillingFrom: "top" as const }
            : {}),
          notes: [
            `Frontal for ${sjbCode}`,
            leafName(leaf),
            leaf.kind === "door" && spec ? `${sideWord} ${unitWord}` : "",
            holeText,
            leaf.part === "top-hung" && leaf.topHungHoles > 0
              ? `Top-hung — ${leaf.topHungHoles} hole${
                  leaf.topHungHoles === 1 ? "" : "s"} in the TOP of the unit, not the side`
              : leaf.kind === "front"
                ? (leaf.part === "lift-up" ? "No drilling" : "No drilling — fixed to the drawer box")
                : "",
            // "Matching carcass" is the default edging and describes the
            // CARCASS front, which the cabinet line already states — on a
            // door (a painted one especially) it reads as a door spec that
            // does not exist. Steve, 2026-10-10. A colour actually chosen
            // still rides along, as before.
            st.edgingColour && st.edgingColour !== SJB_EDGING_MATCHING
              ? `Edging: ${st.edgingColour}` : "",
          ].filter(Boolean).join(" | "),
          listPrice: Number(size.price) || 0, discPct,
          tradePrice: 0, markupMode: "pct", markupPct, markupGbp: 0, retailPrice: 0,
          doorRange: st.frontalRange, doorColour: st.frontalColour,
          // A drawer front has no hinge side at all — "L" would be a claim,
          // not a default.
          hinging: leaf.kind === "front" ? "N"
            : spec?.side === "R" ? "R" : spec?.side === "T" ? "N" : "L",
          sjbGroupId: groupId,
        };
        recalcItem(line, null);
        lines.push(line);
      });

      // Hinge side and the doors themselves on the CABINET description —
      // "900mm Tall Wall LH & RH · 2 × 895 × 447, 2 holes" (Steve,
      // 2026-10-10). Built from the very leaves and specs the door lines
      // above were just built from, so the cabinet can never name a side or
      // a size its own doors do not have. The side follows the door title's
      // rule exactly (sideWord): R is right, T is top-hung and has no hand,
      // anything else is left. Drawer fronts are not doors and are left out.
      const unitDoors = committed.filter((l) => l.kind === "door");
      const hands = new Set<string>();
      const doorSizes = new Map<string, number>();
      for (const l of unitDoors) {
        const sp = drillSpecs[l.doorIndex];
        if (sp?.side !== "T") hands.add(sp?.side === "R" ? "RH" : "LH");
        const holes = sp && sp.mode === "hinge" ? sp.holeQty : 0;
        const key = `${l.curved ? `${l.h} curved` : `${l.h} × ${l.w}`}, ${
          holes ? `${holes} hole${holes === 1 ? "" : "s"}` : "no drilling"}`;
        doorSizes.set(key, (doorSizes.get(key) ?? 0) + 1);
      }
      const handText = ["LH", "RH"].filter((h) => hands.has(h)).join(" & ");
      const doorText = [...doorSizes].map(([k, n]) => `${n} × ${k}`).join(" + ");
      cabinet.desc = [`${cabinet.desc}${handText ? ` ${handText}` : ""}`, doorText]
        .filter(Boolean).join(" · ");

      // Said on the CABINET line too. Top-hung drilling is the one kind that
      // belongs to the unit rather than to a door with a side, and a bridging
      // unit line that says nothing about it reads as undrilled.
      const flapHoles = committed.reduce((n, l) =>
        n + (l.part === "top-hung" ? l.topHungHoles : 0), 0);
      if (flapHoles > 0) {
        cabinet.notes = `${cabinet.notes} | Top-hung flap: ${flapHoles} hole${
          flapHoles === 1 ? "" : "s"} in its top edge, not the side (see the frontal line)`;
      }

      // Drilling stays ONE line for the whole cabinet — a single charge, a
      // single total. Its note summarises the count only; the per-door
      // positions live on each door's own line rather than being concatenated
      // here where they belong to neither.
      const drillTotal = drillingUnitPrice();
      if (drillTotal > 0) {
        const holes = totalHoles();
        const drilledDoors = committed.filter((l) => l.kind === "door").length;
        const flaps = committed.filter((l) => l.part === "top-hung" && l.topHungHoles > 0).length;
        // Say WHAT was drilled. "across 0 doors" is what a bridging unit used
        // to produce once its flap started counting, and a purchase order that
        // names no part for its holes is worse than one that names the wrong
        // number — the factory cannot query a blank.
        const across = [
          drilledDoors ? `${drilledDoors} door${drilledDoors === 1 ? "" : "s"}` : "",
          flaps ? `${flaps} top-hung flap${flaps === 1 ? "" : "s"} (top edge, not the side)` : "",
        ].filter(Boolean).join(" and ");
        const drilling: QuoteItem = {
          id: newId("i"), category: "sundries", supplier: st.frontalLabel,
          code: `${st.frontalRange.replace(/\s+/g, "-").toUpperCase()}-DRILLING`,
          desc: `Drilling charge (${holes} hole${holes === 1 ? "" : "s"})`,
          qty, width: 0, height: 0, depth: 0,
          notes: `${holes} hole${holes === 1 ? "" : "s"} across ${across} for ${sjbCode}${
            drilledDoors ? " · positions on each door line" : ""}`,
          listPrice: drillTotal, discPct: 0,
          tradePrice: 0, markupMode: "pct", markupPct, markupGbp: 0, retailPrice: 0,
          sjbGroupId: groupId,
        };
        recalcItem(drilling, null);
        lines.push(drilling);
      }
    }

    for (const x of pendingExtras) lines.push(addExtra(x, groupId));

    store.set((draft) => {
      const zone = draft.zones.find((z) => z.id === zoneId) ?? draft.zones[0];
      if (zone) zone.items.push(...lines);
      // One Hinge drilling row for the quote, not one per cabinet. The
      // per-leaf hole positions stay on each frontal line — only the priced
      // charge collapses. See drilling-aggregate.ts.
      aggregateDrilling(draft);
    });
    onDone();
  }

  // ── boot ────────────────────────────────────────────────────────────────

  // Registered for as long as this picker is mounted: a part-configured
  // cabinet is real work, and the left panel asks before replacing it. Named
  // by its SKU so the confirm can say what is about to be lost.
  setConfiguratorInProgress(`the ${sjbCode} you are configuring`);

  paint();
  void (async () => {
    const [cols, sups] = await Promise.all([
      loadSjbColours().catch(() => [] as SjbColour[]),
      loadSjbFrontalSuppliers().catch(() => { st.frontalLoadFailed = true; return [] as SjbFrontalSupplier[]; }),
    ]);
    if (!mounted) return;
    colours = cols;
    frontalSuppliers = sups;
    // Resolve the catalogue to open on, in priority order.
    //
    //   1. the quote's remembered catalogue, IF it is still enabled. A quote
    //      can outlive the server config that produced it, and opening on a
    //      catalogue that has since been withdrawn would offer ranges nobody
    //      can order — so it is validated against the live list, not trusted.
    //   2. the only catalogue there is, when there is only one. Preselecting
    //      beats making the retailer answer a one-option control.
    //   3. nothing, and the Catalogue select says so.
    const remembered = st.frontalSlug
      ? sups.find((s) => s.slug === st.frontalSlug)
      : undefined;
    const opening = remembered ?? (sups.length === 1 ? sups[0] : undefined);
    if (opening) {
      st.frontalSlug = opening.slug;
      st.frontalLabel = opening.label;
      // Persist it even when it came from a preselect rather than a click, so
      // the label's "(quote default)" is telling the truth and the next
      // cabinet opens the same way without depending on the count again.
      setSjbDefaults(store, { frontalSlug: opening.slug });
      void loadRanges(opening.slug);
    } else if (st.frontalSlug) {
      // Remembered but no longer offered. Drop it rather than leave a slug
      // selected that the list cannot show, and drop the range and colour
      // with it — they belonged to that catalogue.
      st.frontalSlug = ""; st.frontalLabel = "";
      st.frontalRange = ""; st.frontalColour = ""; st.frontalTier = "";
    }
    paint();
    void reprice();
  })();

  return {
    unmount(): void {
      mounted = false;
      setConfiguratorInProgress(null);
      if (combo) { combo.unmount(); combo = null; }
      if (edgingCombo) { edgingCombo.unmount(); edgingCombo = null; }
      host.innerHTML = "";
    },
  };
}
